import { useTranslation } from 'react-i18next';
import { Badge, Group, Loader, Paper, SimpleGrid, Stack, Table, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { platformApi } from './platformApi';

interface CountRow {
  key: string;
  count: number;
}

interface Overview {
  totalTenants: number;
  byStatus: CountRow[];
  byVertical: CountRow[];
  byPlan: CountRow[];
  newPerMonth: CountRow[];
  moduleAdoption: CountRow[];
}

interface LicenseModule {
  code: string;
  enabledUntil: string | null;
  state: string;
}

interface TenantLicense {
  tenantId: string;
  slug: string;
  businessNameAr: string;
  planCode: string;
  trialEndsAt: string | null;
  trialState: string;
  extraModules: LicenseModule[];
}

const STATE_COLOR: Record<string, string> = {
  active: 'green',
  expiring: 'yellow',
  expired: 'red',
  none: 'gray',
};

export function PlatformReportsPage() {
  const { t } = useTranslation();

  const { data: overview, isLoading: loadingOverview } = useQuery({
    queryKey: ['platform-overview'] as const,
    queryFn: async () => (await platformApi.get<Overview>('/api/platform/reports/overview')).data,
  });
  const { data: licenses, isLoading: loadingLicenses } = useQuery({
    queryKey: ['platform-licenses'] as const,
    queryFn: async () => (await platformApi.get<TenantLicense[]>('/api/platform/licenses')).data,
  });

  if (loadingOverview || loadingLicenses) {
    return (
      <Group justify="center" py="xl">
        <Loader color="wheelsRed" />
      </Group>
    );
  }

  const cards: { title: string; rows: CountRow[] }[] = [
    { title: t('platform.reports.byStatus'), rows: overview?.byStatus ?? [] },
    { title: t('platform.reports.byVertical'), rows: overview?.byVertical ?? [] },
    { title: t('platform.reports.byPlan'), rows: overview?.byPlan ?? [] },
    { title: t('platform.reports.newPerMonth'), rows: overview?.newPerMonth ?? [] },
    { title: t('platform.reports.moduleAdoption'), rows: (overview?.moduleAdoption ?? []).slice(0, 8) },
  ];

  return (
    <Stack gap="md">
      <Group gap="xs">
        <Text fw={600} size="lg">
          {t('platform.reports.title')}
        </Text>
        <Badge variant="light" color="wheels" size="lg" className="font-numeric">
          {overview?.totalTenants ?? 0} {t('platform.reports.businesses')}
        </Badge>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
        {cards.map((card) => (
          <Paper key={card.title} p="md">
            <Text fw={600} size="sm" mb="xs">
              {card.title}
            </Text>
            {card.rows.length === 0 ? (
              <Text size="sm" c="dimmed">
                —
              </Text>
            ) : (
              card.rows.map((row) => (
                <Group key={row.key} justify="space-between" py={2}>
                  <Text size="sm">{row.key}</Text>
                  <Text size="sm" fw={600} className="font-numeric">
                    {row.count}
                  </Text>
                </Group>
              ))
            )}
          </Paper>
        ))}
      </SimpleGrid>

      <Text fw={600} size="lg" mt="sm">
        {t('platform.licenses.title')}
      </Text>
      <Paper p="md">
        <Table verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('platform.tenants.business')}</Table.Th>
              <Table.Th>{t('platform.tenants.plan')}</Table.Th>
              <Table.Th>{t('platform.licenses.trial')}</Table.Th>
              <Table.Th>{t('platform.licenses.modules')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(licenses ?? []).map((license) => (
              <Table.Tr key={license.tenantId}>
                <Table.Td>
                  {license.businessNameAr}
                  <Text size="xs" c="dimmed" dir="ltr">
                    {license.slug}
                  </Text>
                </Table.Td>
                <Table.Td>{license.planCode}</Table.Td>
                <Table.Td>
                  {license.trialState === 'none' ? (
                    <Text size="sm" c="dimmed">
                      —
                    </Text>
                  ) : (
                    <Badge variant="light" color={STATE_COLOR[license.trialState] ?? 'gray'}>
                      {t(`platform.licenses.state.${license.trialState}`)}
                      {license.trialEndsAt
                        ? ` · ${new Date(license.trialEndsAt).toLocaleDateString('ar-PS', { numberingSystem: 'latn' })}`
                        : ''}
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    {license.extraModules.length === 0 && (
                      <Text size="sm" c="dimmed">
                        —
                      </Text>
                    )}
                    {license.extraModules.map((module) => (
                      <Badge
                        key={module.code}
                        variant="light"
                        radius="sm"
                        color={STATE_COLOR[module.state] ?? 'gray'}
                        title={module.enabledUntil ?? ''}
                      >
                        {module.code}
                        {module.enabledUntil
                          ? ` → ${new Date(module.enabledUntil).toLocaleDateString('ar-PS', { numberingSystem: 'latn' })}`
                          : ''}
                      </Badge>
                    ))}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
    </Stack>
  );
}
